// Shared VFD (variable frequency drive) types + value logic, used by the admin
// "VFD Drives" tab and the client Live PLC Data → VFD Drives view.
//
// The bridge only decodes real registers (scale / signed / 32-bit / bit / mask) and
// sends plain numbers. Calculated items (power factor, kW, run status, RPM from Hz)
// are computed here from those numbers, so the formulas live in one place.

import { API_BASE } from './api-base';

export type VfdItemKind = 'number' | 'bit' | 'nonzero' | 'derived';
export type VfdRole =
  | '' | 'frequency' | 'voltage' | 'current' | 'power' | 'pf' | 'rpm'
  | 'run' | 'fault' | 'torque' | 'dcbus' | 'temperature' | 'setpoint';
export type VfdFormula = 'cos_deg' | 'power_3ph' | 'pf_from_power' | 'above' | 'rpm_from_freq';

export interface VfdItem {
  id: string;
  name: string;
  role: VfdRole;
  kind: VfdItemKind;
  unit?: string;
  decimals?: number;
  hidden?: boolean;
  note?: string;
  // register items
  addr?: number | null;
  func?: 3 | 4;
  words?: 1 | 2;
  signed?: boolean;
  wordOrder?: 'big' | 'little';
  scale?: number;
  // flag items
  bit?: number;
  mask?: number;
  trueText?: string;
  falseText?: string;
  badWhenTrue?: boolean;
  showCode?: boolean;
  // derived items
  formula?: VfdFormula;
  inputs?: Record<string, string>;
  threshold?: number;
  poles?: number;
}

export interface VfdRatings {
  ratedKw?: number | null;
  ratedCurrent?: number | null;
  ratedVoltage?: number | null;
  ratedRpm?: number | null;
  maxFrequency?: number | null;
}

export interface VfdDrive {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  location?: string;
  brand?: string;
  modelId?: string;
  modelName?: string;
  connectionType: 'rtu' | 'tcp';
  serialPort?: string;
  baudRate?: number;
  dataBits?: number;
  stopBits?: number;
  parity?: 'none' | 'even' | 'odd';
  slaveId?: number;
  ipAddress?: string;
  port?: number;
  pollIntervalMs?: number;
  timeoutMs?: number;
  ratings: VfdRatings;
  items: VfdItem[];
  isActive?: boolean;
  bridgeId?: string | null;
  updatedAt?: string;
}

export interface VfdPresetModel {
  id: string;
  name: string;
  status: 'verified' | 'partial' | 'needs_manual' | 'custom' | string;
  note?: string;
  source?: string;
  items: VfdItem[];
}
export interface VfdPresetBrand { brand: string; models: VfdPresetModel[] }

export const VFD_ROLE_OPTIONS: { value: VfdRole; label: string }[] = [
  { value: '', label: 'Other parameter' },
  { value: 'frequency', label: 'Output frequency' },
  { value: 'rpm', label: 'Motor speed' },
  { value: 'current', label: 'Output current' },
  { value: 'voltage', label: 'Output voltage' },
  { value: 'power', label: 'Output power' },
  { value: 'pf', label: 'Power factor' },
  { value: 'run', label: 'Run status' },
  { value: 'fault', label: 'Fault status' },
  { value: 'torque', label: 'Torque' },
  { value: 'dcbus', label: 'DC bus voltage' },
  { value: 'temperature', label: 'Temperature' },
  { value: 'setpoint', label: 'Frequency setpoint' },
];

export const VFD_FORMULAS: { value: VfdFormula; label: string; inputs: { key: string; label: string }[] }[] = [
  { value: 'pf_from_power', label: 'Power factor from kW, V, A', inputs: [{ key: 'power', label: 'Power (kW)' }, { key: 'voltage', label: 'Voltage' }, { key: 'current', label: 'Current' }] },
  { value: 'cos_deg', label: 'Power factor = cos(angle°)', inputs: [{ key: 'angle', label: 'PF angle (deg)' }] },
  { value: 'power_3ph', label: 'kW = √3 × V × I × PF', inputs: [{ key: 'voltage', label: 'Voltage' }, { key: 'current', label: 'Current' }, { key: 'pf', label: 'Power factor' }] },
  { value: 'above', label: 'Running when value > threshold', inputs: [{ key: 'source', label: 'Source value' }] },
  { value: 'rpm_from_freq', label: 'RPM = 120 × Hz ÷ poles', inputs: [{ key: 'freq', label: 'Frequency' }] },
];

// Status colors are reserved for state and always shown with an icon + label.
export const VFD_STATUS_COLORS = {
  good: '#0ca30c',
  warning: '#fab219',
  critical: '#d03b3b',
  neutral: '#64748b',
};

export interface VfdComputedValue {
  item: VfdItem;
  num: number | null;      // numeric value (flags: 1/0)
  flag?: boolean;          // for bit / nonzero / above
  code?: number;           // fault/status code for nonzero items
  text: string;            // formatted display text
  state: 'good' | 'bad' | 'warn' | 'neutral' | 'na' | 'error';
  calc: boolean;           // calculated rather than read
  error?: string;
}

const isNum = (v: any): v is number => typeof v === 'number' && Number.isFinite(v);

const fmt = (v: number, decimals = 2) =>
  v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

function flagValue(item: VfdItem, flag: boolean, calc: boolean, code?: number): VfdComputedValue {
  const bad = !!item.badWhenTrue;
  let text = flag ? (item.trueText || 'ON') : (item.falseText || 'OFF');
  if (item.showCode && flag && isNum(code)) text += ` (${code})`;
  return {
    item,
    num: flag ? 1 : 0,
    flag,
    code,
    text,
    state: bad ? (flag ? 'bad' : 'good') : (flag ? 'good' : 'neutral'),
    calc,
  };
}

function derive(it: VfdItem, nums: Record<string, number>): { num?: number; flag?: boolean } | null {
  const g = (k: string) => nums[it.inputs?.[k] || ''];
  const S3 = Math.sqrt(3);
  switch (it.formula) {
    case 'cos_deg': {
      const a = g('angle');
      if (!isNum(a)) return null;
      return { num: Math.min(1, Math.max(0, Math.cos((a * Math.PI) / 180))) };
    }
    case 'power_3ph': {
      const v = g('voltage'), i = g('current'), pf = g('pf');
      if (!isNum(v) || !isNum(i) || !isNum(pf)) return null;
      return { num: (S3 * v * i * pf) / 1000 };
    }
    case 'pf_from_power': {
      const p = g('power'), v = g('voltage'), i = g('current');
      if (!isNum(p) || !isNum(v) || !isNum(i) || v * i <= 0) return null;
      return { num: Math.min(1, Math.max(0, (p * 1000) / (S3 * v * i))) };
    }
    case 'above': {
      const s = g('source');
      if (!isNum(s)) return null;
      return { flag: s > (it.threshold ?? 0) };
    }
    case 'rpm_from_freq': {
      const f = g('freq');
      if (!isNum(f)) return null;
      return { num: (120 * f) / (it.poles || 4) };
    }
    default:
      return null;
  }
}

/**
 * Turn the bridge's decoded numbers into display values for every item.
 * `values` / `errors` come straight from a `vfd_live_data` event.
 */
export function computeVfdValues(
  items: VfdItem[],
  values: Record<string, number | null> | undefined,
  errors: Record<string, string> | undefined,
): Record<string, VfdComputedValue> {
  const out: Record<string, VfdComputedValue> = {};
  const nums: Record<string, number> = {};
  const vals = values || {};
  const errs = errors || {};

  for (const it of items) {
    if (it.kind === 'derived') continue;
    // The bridge never sends a value for an item with no address; simulation mode does.
    if ((it.addr === null || it.addr === undefined) && !isNum(vals[it.name])) {
      out[it.name] = { item: it, num: null, text: 'N/A', state: 'na', calc: false, error: 'No register address set' };
      continue;
    }
    const v = vals[it.name];
    if (!isNum(v)) {
      out[it.name] = {
        item: it,
        num: null,
        text: errs[it.name] ? 'ERR' : '—',
        state: errs[it.name] ? 'error' : 'na',
        calc: false,
        error: errs[it.name],
      };
      continue;
    }
    if (it.kind === 'bit') {
      out[it.name] = flagValue(it, v !== 0, false);
    } else if (it.kind === 'nonzero') {
      out[it.name] = flagValue(it, v !== 0, false, v);
    } else {
      nums[it.name] = v;
      out[it.name] = { item: it, num: v, text: fmt(v, it.decimals ?? 2), state: 'neutral', calc: false };
    }
  }

  // Calculated items in list order, so one can feed the next (PF → kW).
  for (const it of items) {
    if (it.kind !== 'derived') continue;
    const d = derive(it, nums);
    if (!d) {
      out[it.name] = { item: it, num: null, text: '—', state: 'na', calc: true };
    } else if (typeof d.flag === 'boolean') {
      out[it.name] = flagValue(it, d.flag, true);
    } else if (isNum(d.num)) {
      nums[it.name] = d.num;
      out[it.name] = { item: it, num: d.num, text: fmt(d.num, it.decimals ?? 2), state: 'neutral', calc: true };
    }
  }
  return out;
}

export const findByRole = (items: VfdItem[], computed: Record<string, VfdComputedValue>, role: VfdRole) => {
  const item = items.find((it) => it.role === role);
  return item ? computed[item.name] || null : null;
};

export type VfdDriveState = 'running' | 'stopped' | 'fault' | 'offline' | 'waiting';

export function getDriveState(
  items: VfdItem[],
  computed: Record<string, VfdComputedValue>,
  online: boolean,
  hasData: boolean,
): VfdDriveState {
  if (!online) return hasData ? 'offline' : 'waiting';
  const fault = findByRole(items, computed, 'fault');
  if (fault?.flag && fault.item.badWhenTrue !== false) return 'fault';
  const run = findByRole(items, computed, 'run');
  if (run && typeof run.flag === 'boolean') return run.flag ? 'running' : 'stopped';
  const freq = findByRole(items, computed, 'frequency');
  if (isNum(freq?.num)) return Math.abs(freq!.num!) > 0.05 ? 'running' : 'stopped';
  return 'stopped';
}

// Accepts 8451, "8451", "0x2103", "2103H". Returns null for blank, NaN for invalid.
export function parseRegisterInput(raw: string): number | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  if (/^0x[0-9a-f]+$/i.test(s)) return Number.parseInt(s.slice(2), 16);
  if (/^[0-9a-f]+h$/i.test(s)) return Number.parseInt(s.slice(0, -1), 16);
  if (/^\d+$/.test(s)) return Number.parseInt(s, 10);
  return Number.NaN;
}

export const formatRegister = (addr?: number | null) =>
  addr === null || addr === undefined ? '' : `${addr}`;

export const hexRegister = (addr?: number | null) =>
  addr === null || addr === undefined ? '' : `0x${addr.toString(16).toUpperCase().padStart(4, '0')}`;

// ------------------------------------------------------------------ API
const authHeaders = (token: string) => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

async function request<T>(url: string, token: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, { ...init, headers: { ...authHeaders(token), ...(init.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Request failed (HTTP ${res.status})`);
  return body as T;
}

export const vfdApi = {
  listForClient: (token: string, clientId: string) =>
    request<{ drives: VfdDrive[] }>(`/api/clients/${clientId}/vfds`, token).then((r) => r.drives || []),
  adminList: (token: string, clientId: string) =>
    request<{ drives: VfdDrive[] }>(`/api/admin/clients/${clientId}/vfds`, token).then((r) => r.drives || []),
  presets: (token: string) =>
    request<{ brands: VfdPresetBrand[] }>('/api/admin/vfd-presets', token).then((r) => r.brands || []),
  create: (token: string, clientId: string, drive: Partial<VfdDrive>) =>
    request<{ drive: VfdDrive }>(`/api/admin/clients/${clientId}/vfds`, token, { method: 'POST', body: JSON.stringify(drive) }).then((r) => r.drive),
  update: (token: string, clientId: string, id: string, drive: Partial<VfdDrive>) =>
    request<{ drive: VfdDrive }>(`/api/admin/clients/${clientId}/vfds/${id}`, token, { method: 'PUT', body: JSON.stringify(drive) }).then((r) => r.drive),
  remove: (token: string, clientId: string, id: string) =>
    request<{ success: boolean }>(`/api/admin/clients/${clientId}/vfds/${id}`, token, { method: 'DELETE' }),
};
